import { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { AdminButton } from "../../SuperAdmin/SharedComponents/ListView";

const toEditorState = (html) => {
  const block = htmlToDraft(html || "");
  return EditorState.createWithContent(ContentState.createFromBlockArray(block.contentBlocks, block.entityMap));
};

const toHtml = (editorState) => draftToHtml(convertToRaw(editorState.getCurrentContent()));

// No images or embeds: contract text may only carry formatting (see SafeHtml).
const TOOLBAR = {
  options: ["inline", "blockType", "fontSize", "fontFamily", "list", "textAlign", "colorPicker", "link", "remove", "history"],
};

/**
 * Create or edit a contract (name + HTML text).
 * The HTML is produced from the editor's CURRENT state at save time, so the last keystroke
 * is never lost (the shared WysiwygEditor reports its previous state on change).
 * Saving an untouched contract sends nothing: the editor re-formats stored HTML, so comparing
 * against the stored text would create a new version and make everyone accept again.
 */
export default function ContractEditorModal({ show, contract, saving, error, onHide, onSave }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [initialHtml, setInitialHtml] = useState("");

  useEffect(() => {
    if (!show) return;
    const initial = contract ? toEditorState(contract.content) : EditorState.createEmpty();
    setName(contract?.name ?? "");
    setEditorState(initial);
    setInitialHtml(toHtml(initial));
  }, [show, contract]);

  const hasText = editorState.getCurrentContent().hasText();
  const canSave = !saving && name.trim().length > 0 && hasText;

  const handleSave = () => {
    if (!canSave) return;
    const content = toHtml(editorState);
    if (contract && name.trim() === contract.name && content === initialHtml) {
      onHide();
      return;
    }
    onSave({ name: name.trim(), content });
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{contract ? t("editContract") : t("addContract")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="contract-name">
          <Form.Label>{t("contractName")}</Form.Label>
          <Form.Control
            value={name}
            maxLength={200}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          />
        </Form.Group>

        <Form.Label>{t("contractContent")}</Form.Label>
        <div className="contract-editor">
          <Editor editorState={editorState} onEditorStateChange={setEditorState} toolbar={TOOLBAR} />
        </div>

        {contract && <p className="contract-edit-warning">{t("contractEditWarning")}</p>}
        {error && <div className="text-danger mt-2">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <AdminButton variant="outline" onClick={onHide} disabled={saving}>
          {t("giveUp")}
        </AdminButton>
        <AdminButton onClick={handleSave} disabled={!canSave}>
          {t("saveChangesButtonLabel")}
        </AdminButton>
      </Modal.Footer>
    </Modal>
  );
}
